import React from 'react';

/**
 * Badge component props interface
 */
export interface BadgeProps {
  /** Badge content */
  children: React.ReactNode;
  /** Visual variant of the badge */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  /** Size of the badge */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the badge should be rounded (pill shape) */
  rounded?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Design system colors
 */
const COLORS = {
  default: {
    background: '#F4F4F6',
    color: '#8C929C',
  },
  primary: {
    background: '#5E64FF',
    color: '#FFFFFF',
  },
  secondary: {
    background: '#E5E7EB',
    color: '#6B7280',
  },
  success: {
    background: '#10B981',
    color: '#FFFFFF',
  },
  warning: {
    background: '#F59E0B',
    color: '#FFFFFF',
  },
  danger: {
    background: '#F04438',
    color: '#FFFFFF',
  },
} as const;

/**
 * Badge component following design system standards
 * Used for labels, status indicators, and counts
 * 
 * @example
 * ```tsx
 * <Badge variant="primary">New</Badge>
 * <Badge variant="success" size="sm">Active</Badge>
 * <Badge variant="danger" rounded>3</Badge>
 * ```
 */
const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  rounded = false,
  className,
  style,
}) => {
  const getBadgeStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      borderRadius: rounded ? '9999px' : '6px',
      border: 'none',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      lineHeight: 1,
      fontFamily: 'inherit',
    };

    const sizeStyles: Record<NonNullable<BadgeProps['size']>, React.CSSProperties> = {
      sm: {
        fontSize: '11px',
        padding: '2px 6px',
        minHeight: '18px',
      },
      md: {
        fontSize: '12px',
        padding: '4px 8px',
        minHeight: '22px',
      },
      lg: {
        fontSize: '13px',
        padding: '6px 12px',
        minHeight: '26px',
      },
    };

    const variantStyles = COLORS[variant];

    return {
      ...baseStyles,
      ...sizeStyles[size],
      backgroundColor: variantStyles.background,
      color: variantStyles.color,
      ...style,
    };
  };

  return (
    <span
      style={getBadgeStyles()}
      className={className}
    >
      {children}
    </span>
  );
};

Badge.displayName = 'Badge';

export default Badge;
